import axios from 'axios'
import { getToken, getFirebaseIdToken } from './token'

const BASE_URL = process.env.VUE_APP_ERACUN_API_URL || 'https://europe-west1-ai-fisk-prod.cloudfunctions.net/documents'
const CONFIG_BASE_URL = process.env.VUE_APP_ERACUN_CONFIG_API_URL || 'https://europe-west1-ai-fisk-prod.cloudfunctions.net/eracunConfig'

const eracunClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

const configClient = axios.create({
  baseURL: CONFIG_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

eracunClient.interceptors.request.use(async (config) => {
  try {
    const token = await getToken('documents')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    console.error('Error getting token for eRacun API:', error)
  }
  return config
})

eracunClient.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage = error.message || 'Network error occurred'

    if (error.response?.data) {
      const responseData = error.response.data

      // Extract nested error from API response
      if (responseData.message && typeof responseData.message === 'string') {
        try {
          // Try to parse JSON from message field
          const nestedError = JSON.parse(responseData.message.replace(/\r:/g, '').replace(/\r/g, ''))
          errorMessage = nestedError.details || nestedError.error || responseData.message
        } catch (parseError) {
          // If parsing fails, use the message as is
          errorMessage = responseData.message
        }
      } else if (responseData.error) {
        errorMessage = responseData.error
      } else if (responseData.details) {
        errorMessage = responseData.details
      }
    }

    console.error('eRacun API Error:', errorMessage)

    // Attach the parsed error message to the error object
    error.parsedMessage = errorMessage

    return Promise.reject(error)
  }
)

// Config client interceptors - use Firebase Auth ID token directly
configClient.interceptors.request.use(async (config) => {
  try {
    const token = await getFirebaseIdToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    console.error('Error getting token for eRacun Config API:', error)
  }
  return config
})

configClient.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage = error.message || 'Network error occurred'

    if (error.response?.data) {
      const responseData = error.response.data
      errorMessage = responseData.error || responseData.details || responseData.message || errorMessage
    }

    console.error('eRacun Config API Error:', errorMessage)
    error.parsedMessage = errorMessage

    return Promise.reject(error)
  }
)

function buildQueryParams (params) {
  const searchParams = new URLSearchParams()

  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      searchParams.append(key, params[key])
    }
  })

  return searchParams.toString()
}

export const eracunApi = {
  async getDocuments (direction, filters = {}, pagination = {}) {
    const params = {
      direction,
      ...filters,
      ...pagination
    }

    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/?${queryString}`)
    return response.data
  },

  async getDocument (id, companyId) {
    const params = { companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/${id}?${queryString}`)
    return response.data
  },

  // Metode koje koriste documentId i issueYear kao query parametre (za račune iz receipts kolekcije)
  async getDocumentByDocumentId (documentId, issueYear, companyId) {
    const params = { documentId, issueYear, companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/?${queryString}`)
    return response.data
  },

  async getDocumentPdfByDocumentId (documentId, issueYear, companyId) {
    const params = { documentId, issueYear, companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/pdf?${queryString}`)
    return response.data
  },

  async getDocumentXmlByDocumentId (documentId, issueYear, companyId) {
    const params = { documentId, issueYear, companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/xml?${queryString}`)
    return response.data
  },

  async updateDocumentStatus (id, payload, companyId, updateMask) {
    const params = { companyId }

    if (updateMask && updateMask.length > 0) {
      params.update_mask = updateMask.join(',')
    }

    const queryString = buildQueryParams(params)
    const response = await eracunClient.patch(`/${id}?${queryString}`, payload)
    return response.data
  },

  async updateDocumentStatusByDocumentId (documentId, issueYear, payload, companyId, updateMask) {
    const params = { documentId, issueYear, companyId }

    if (updateMask && updateMask.length > 0) {
      params.update_mask = updateMask.join(',')
    }

    const queryString = buildQueryParams(params)
    const response = await eracunClient.patch(`/?${queryString}`, payload)
    return response.data
  },

  async getDocumentPdf (id, companyId) {
    const params = { companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/${id}/pdf?${queryString}`)
    return response.data
  },

  async getDocumentXml (id, companyId) {
    const params = { companyId }
    const queryString = buildQueryParams(params)
    const response = await eracunClient.get(`/${id}/xml?${queryString}`)
    return response.data
  },

  async sendDocument (payload) {
    const response = await eracunClient.post('/send', payload)
    return response.data
  },

  async validateDocument (payload) {
    const response = await eracunClient.post('/validate', payload)
    return response.data
  },

  // eRacun Configuration Management
  async createConfig (companyId, config) {
    const payload = {
      company_id: companyId,
      ...config
    }
    const response = await configClient.post('/', payload)
    return response.data
  },

  async updateConfig (companyId, updates) {
    const payload = {
      company_id: companyId,
      ...updates
    }
    const response = await configClient.patch('/', payload)
    return response.data
  },

  async getConfig (companyId) {
    const params = { company_id: companyId }
    const queryString = buildQueryParams(params)
    const response = await configClient.get(`/?${queryString}`)
    return response.data
  }
}

export default eracunApi
